/**
 * DpeCard — diagnostics de performance énergétique du voisinage.
 *
 * UX spec :
 *   - Sous-titre : distribution synthétique ("médiane D · 200+ DPE · rayon 500 m").
 *   - Histogramme A→G coloré ARRÊTÉ DPE (vert A, jaune D, rouge G).
 *   - Liste des 8 DPE les plus récents : lettre, adresse courte, surface,
 *     distance, année.
 *
 * On rappelle dans le footer la portée : il s'agit des DPE voisins du bien,
 * pas du bien lui-même — utile pour qualifier la typologie du quartier
 * (neuf vs. bâti ancien, dominantes C vs. E, etc.).
 */

import type { FicheDoc } from "../../lib/fiches";
import {
  useDpe,
  type DpeClass,
  type DpeData,
  type DpeItem,
} from "../../lib/env/dpe";
import { TIER_LABEL } from "../../lib/env/localityTier";
import { EnvCardShell } from "./EnvCardShell";
import { Ic } from "../icons";

type Props = { fiche: FicheDoc };

// DPE colour ramp — matches the official ADEME scale : A green, G red.
// Using Tailwind-like hex values so the card renders predictably without
// pulling in the project's design tokens for what's essentially a one-off
// legend.
const CLASS_COLORS: Record<DpeClass, string> = {
  A: "#2F7C3A",
  B: "#63A93D",
  C: "#B9C23B",
  D: "#F1D43B",
  E: "#EFA43B",
  F: "#E36F2F",
  G: "#C53030",
};

const CLASSES: DpeClass[] = ["A", "B", "C", "D", "E", "F", "G"];

export function DpeCard({ fiche }: Props) {
  const state = useDpe(fiche.id, fiche.geocode);
  const subtitle = state.status === "ready" ? summarise(state.data) : undefined;

  return (
    <EnvCardShell
      icon={<Ic.Bolt size={18} />}
      title="DPE du voisinage"
      subtitle={subtitle}
      state={state}
      footer={
        <>
          Source : ADEME · dataset <code>dpe03existant</code>. Échantillon du
          bâti voisin — ne préjuge pas du DPE du bien concerné.
        </>
      }
    >
      {state.status === "ready" && <DpeBody data={state.data} />}
    </EnvCardShell>
  );
}

// ─── Summary ─────────────────────────────────────────────────────────────

function summarise(d: DpeData): string {
  const radiusLabel = `${formatRadius(d.radius_m)} · ${TIER_LABEL[d.tier]}`;
  if (d.total === 0) {
    return `Aucun DPE · ${radiusLabel}`;
  }
  const totalLabel = d.total >= 200 ? "200+ DPE" : `${d.total} DPE`;
  const medianLabel = d.median ? `médiane ${d.median}` : "";
  return [medianLabel, totalLabel, radiusLabel].filter(Boolean).join(" · ");
}

// ─── Body ────────────────────────────────────────────────────────────────

function DpeBody({ data }: { data: DpeData }) {
  if (data.total === 0) {
    return (
      <div className="text-[13px] text-ink-600 py-2">
        Aucun DPE enregistré à proximité immédiate. Le bâti y est soit très
        récent (pas encore diagnostiqué) soit très diffus.
      </div>
    );
  }
  const max = Math.max(...CLASSES.map((c) => data.distribution[c]));

  return (
    <div className="space-y-4">
      {/* Histogram */}
      <div>
        <div className="grid grid-cols-7 gap-1.5 items-end h-24">
          {CLASSES.map((c) => {
            const n = data.distribution[c];
            const h = max > 0 ? Math.round((n / max) * 100) : 0;
            return (
              <div key={c} className="flex flex-col items-center justify-end h-full">
                <div
                  className="w-full rounded-t-md transition-[height] duration-300"
                  style={{
                    height: `${Math.max(h, n > 0 ? 4 : 0)}%`,
                    backgroundColor: CLASS_COLORS[c],
                    opacity: n === 0 ? 0.2 : 1,
                  }}
                />
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-7 gap-1.5 mt-1">
          {CLASSES.map((c) => (
            <div key={c} className="text-center">
              <div className="text-[11px] font-semibold text-ink-700">{c}</div>
              <div className="text-[10.5px] text-ink-500 tabular">
                {data.distribution[c]}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent list */}
      {data.recent.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.1em] text-ink-500 font-medium mb-1.5">
            Derniers diagnostics
          </div>
          <ul className="space-y-1">
            {data.recent.map((it) => (
              <DpeRow key={it.id} item={it} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function DpeRow({ item }: { item: DpeItem }) {
  return (
    <li className="flex items-center justify-between gap-2 py-1">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span
          className="inline-flex items-center justify-center w-6 h-6 rounded-md text-[11px] font-semibold text-white shrink-0"
          style={{ backgroundColor: CLASS_COLORS[item.classe] }}
        >
          {item.classe}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] text-ink-900 truncate">
            {item.address || "Adresse non précisée"}
          </div>
          <div className="text-[11px] text-ink-500">
            {formatRowMeta(item)}
          </div>
        </div>
      </div>
      <span className="text-[11.5px] text-ink-500 tabular shrink-0 w-12 text-right">
        {formatDistance(item.distance_m)}
      </span>
    </li>
  );
}

// ─── Small helpers ───────────────────────────────────────────────────────

function formatRowMeta(it: DpeItem): string {
  const parts: string[] = [];
  if (it.surface_m2) parts.push(`${it.surface_m2} m²`);
  if (it.date) {
    const y = it.date.slice(0, 4);
    if (/^\d{4}$/.test(y)) parts.push(y);
  }
  return parts.join(" · ");
}

function formatDistance(m: number): string {
  if (m < 950) return `${m} m`;
  return `${(m / 1000).toFixed(1).replace(".", ",")} km`;
}

function formatRadius(m: number): string {
  if (m < 1000) return `${m} m`;
  if (m % 1000 === 0) return `${m / 1000} km`;
  return `${(m / 1000).toFixed(1).replace(".", ",")} km`;
}
